import React from 'react';
import * as Sentry from '@sentry/react';

type ErrorBoundaryProps = { children: React.ReactNode };
type ErrorBoundaryState = { hasError: boolean; summary?: string };

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // Evitar logging ruidoso en producción
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('Error capturado por ErrorBoundary:', error);
    } else {
      try {
        const summary = (error instanceof Error) ? `${error.name}: ${error.message}` : 'Unknown error';
        const body = JSON.stringify({ level: 'error', where: 'ErrorBoundary', summary });
        if (navigator?.sendBeacon) {
          navigator.sendBeacon('/api/health', body);
        }
      } catch {}
    }
    let summary: string | undefined;
    try {
      if (error instanceof Error) {
        summary = `${error.name}: ${error.message}`;
      } else if (typeof error === 'string') {
        summary = error;
      } else {
        summary = 'Error desconocido';
      }
    } catch {
      summary = undefined;
    }

    this.setState({ hasError: true, summary });

    try { Sentry.captureException(error as any); } catch {}
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24 }}>
          <h2>Ocurrió un error inesperado</h2>
          <p>Por favor, recargá la página. Si el problema persiste, intenta más tarde.</p>
          {this.state.summary && (
            <p style={{ marginTop: 12, fontSize: 12, color: '#555' }}>
              Detalle técnico: {this.state.summary}
            </p>
          )}
          <button onClick={() => window.location.reload()}>Recargar</button>
        </div>
      );
    }
    return this.props.children as React.ReactElement;
  }
}

export default ErrorBoundary;



